import { forwardRef } from 'react';
import type { ReactNode, ForwardedRef, ComponentPropsWithoutRef } from 'react';

import type { ColorType } from '@lib/types';
import { cn, cva, type VariantProps, setThisClass } from '@lib/utils';

const avatarIndicatorVariants = cva(
  [
    // Layout
    'absolute rounded-full',
    // Size (always consistent)
    'h-3 w-3',
    // Ring
    'ring-2 ring-white dark:ring-dark-800',
  ],
  {
    variants: {
      position: {
        'top-left': 'top-0 left-0',
        'top-right': 'top-0 right-0',
        'bottom-left': 'bottom-0 left-0',
        'bottom-right': 'bottom-0 right-0',
      },
      ping: {
        true: '',
        false: '',
      },
    },
    defaultVariants: {
      position: 'bottom-right',
      ping: false,
    },
  }
);

export type AvatarIndicatorProps = {
  /** Color of the indicator */
  color?: ColorType;
  /** Position of the indicator */
  position?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
  /** Whether the indicator should pulse */
  ping?: boolean;
  children?: ReactNode;
} & VariantProps<typeof avatarIndicatorVariants> &
  ComponentPropsWithoutRef<'div'>;

const AvatarIndicator = forwardRef<HTMLDivElement, AvatarIndicatorProps>(
  (
    { color = 'neutral', position = 'bottom-right', ping = false, className, children, ...rest },
    ref: ForwardedRef<HTMLDivElement>
  ) => {
    return (
      <div
        className={cn(
          avatarIndicatorVariants({ position, ping }),
          color === 'neutral' ? 'bg-line' : [setThisClass(color), 'bg-this-solid'],
          className
        )}
        {...rest}
        ref={ref}
      >
        {ping && (
          <span className="absolute inset-0 inline-flex h-full w-full animate-ping rounded-full bg-inherit opacity-80" />
        )}
        {children}
      </div>
    );
  }
);

AvatarIndicator.displayName = 'AvatarIndicator';

export { AvatarIndicator, avatarIndicatorVariants };
